// organize-imports-ignore

import "./allowExternalDomManipulators";
import React from "react";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router";
import { ErrorCatcher } from "./common/error-catcher";
import { configureStore } from "./store";
import { AppNavigateBridge } from "./utils/app-navigate";
import { lazyWithRetry } from "./utils/lazy";
import { NavigationGuardProvider } from "./utils/navigation-guard";
import { IS_PROD, POSTHOG_KEY } from "./globals";
import "./style/tailwind.css";

const App = lazyWithRetry(() => import("./App"));
const LoginEntry = lazyWithRetry(() => import("./LoginEntry"));
const MobileEntry = lazyWithRetry(() => import("./MobileEntry"));
const BranchLinkEntry = lazyWithRetry(() => import("./BranchLinkEntry"));
const ThreeDSCallbackEntry = lazyWithRetry(
  () => import("./ThreeDSCallbackEntry")
);

const container = document.getElementById("root");
if (container) {
  const root = createRoot(container);

  // This app runs react-router in **Declarative Mode** (<BrowserRouter> +
  // <Routes>/<Route> in JSX, useNavigate()/useLocation()/<Link> hooks) -
  // one of react-router's three supported modes (Declarative/Data/
  // Framework as of v7+), not a legacy path. We don't use Data Mode
  // (createBrowserRouter/RouterProvider, loader/action route config) or
  // Framework Mode (the @react-router/dev Vite plugin, file-based route
  // modules, built-in SSR - not installed in this app). That makes this a
  // pure client-rendered SPA: no server involvement in rendering, all
  // data fetched ourselves (Redux), no SSR today. Adopting SSR later
  // (e.g. for the planned Yii vanilla-views lander migration) means
  // adopting Framework Mode specifically - a separate, scoped migration,
  // not a flag to flip on top of this setup.
  //
  // Every branch below gets its own <BrowserRouter> (all backed by the same
  // browser History API - safe since exactly one of these branches ever
  // mounts per page load, so there's never more than one active at once).
  // Standalone entry points (login/mobile/branch/3ds-callback) don't run the
  // main App/Routes tree, but still render shared components (e.g. Button)
  // that call react-router hooks like useLocation() unconditionally - those
  // hooks throw without a Router ancestor, so every standalone entry needs
  // one too, even though none of them define their own <Routes>.
  //
  // <BrowserRouter> wraps <ErrorCatcher>, not the other way around: ErrorCatcher's
  // own fallback UI renders <Button> (Reload/Go home), which hits that same
  // useLocation() call. If ErrorCatcher were outermost, catching an error
  // anywhere below would unmount the Router along with the crashed subtree,
  // so the fallback's Button would throw with no Router ancestor either -
  // and since ErrorCatcher is the outermost boundary, that second throw has
  // nothing left to catch it, unmounting the whole root to a blank page
  // instead of showing the fallback. See fix/error-catcher-outside-router.
  //
  // <NavigationGuardProvider> sits right inside <BrowserRouter>, same
  // reasoning: <Button> now also calls useNavigationGuard()
  // unconditionally (see src/utils/navigation-guard.tsx), so it needs the
  // same "outside ErrorCatcher" placement or the fallback UI's own Button
  // would throw with no provider ancestor either.

  if (window.location.pathname.startsWith("/login")) {
    root.render(
      // <React.StrictMode>
      <BrowserRouter>
        <NavigationGuardProvider>
          <ErrorCatcher>
            <React.Suspense fallback={null}>
              <LoginEntry />
            </React.Suspense>
          </ErrorCatcher>
        </NavigationGuardProvider>
      </BrowserRouter>
      // </React.StrictMode>
    );
  } else if (window.location.pathname.startsWith("/mobile")) {
    root.render(
      // <React.StrictMode>
      <BrowserRouter>
        <NavigationGuardProvider>
          <ErrorCatcher>
            <React.Suspense fallback={null}>
              <MobileEntry />
            </React.Suspense>
          </ErrorCatcher>
        </NavigationGuardProvider>
      </BrowserRouter>
      // </React.StrictMode>
    );
  } else if (window.location.pathname.startsWith("/branch")) {
    root.render(
      // <React.StrictMode>
      <BrowserRouter>
        <NavigationGuardProvider>
          <ErrorCatcher>
            <React.Suspense fallback={null}>
              <BranchLinkEntry />
            </React.Suspense>
          </ErrorCatcher>
        </NavigationGuardProvider>
      </BrowserRouter>
      // </React.StrictMode>
    );
  } else if (window.location.pathname.startsWith("/3ds-callback")) {
    root.render(
      // <React.StrictMode>
      <BrowserRouter>
        <NavigationGuardProvider>
          <ErrorCatcher>
            <React.Suspense fallback={null}>
              <ThreeDSCallbackEntry />
            </React.Suspense>
          </ErrorCatcher>
        </NavigationGuardProvider>
      </BrowserRouter>
      // </React.StrictMode>
    );
  } else {
    const store = configureStore();
    posthog.init(POSTHOG_KEY!, {
      api_host: "https://app.posthog.com",
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      capture_performance: false,
      debug: !IS_PROD,
    });

    root.render(
      // <React.StrictMode>
      // <BrowserRouter> outermost, same reasoning as the standalone entries
      // above: it must wrap <ErrorCatcher>, not sit inside it, so the
      // fallback UI's <Button> (which calls useLocation()) still has a
      // Router ancestor when something below actually crashes.
      <BrowserRouter>
        <NavigationGuardProvider>
          {/* Only needed here, not the standalone entries below - its two
              current callers (websocket, page-and-routing helpers) are only
              reachable from the main App tree. See src/utils/app-navigate.tsx. */}
          <AppNavigateBridge />
          <ErrorCatcher showHomeLink>
            {/* stabilityCheck="always" (react-redux default is "once", dev-only
              either way, no-op in production builds): makes react-redux warn
              in the console whenever a useSelector call returns a new
              reference for identical state - exactly the
              `useSelector((state) => ({...}))` combined-object-literal
              pattern that silently defeated several components' React.memo
              by making them re-render on every dispatch app-wide (see
              perf/selector-cascade-and-list-item-fixes). "once" only samples
              each selector's first call; "always" catches it regardless of
              when it starts happening. */}
            <Provider store={store} stabilityCheck="always">
              <PostHogProvider client={posthog}>
                <React.Suspense fallback={null}>
                  <App />
                </React.Suspense>
              </PostHogProvider>
            </Provider>
          </ErrorCatcher>
        </NavigationGuardProvider>
      </BrowserRouter>
      // </React.StrictMode>
    );
  }
}
